import { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Checkbox,
  FormControl,
  IconButton,
  InputLabel,
  ListSubheader,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { DeleteOutline, Visibility } from '@mui/icons-material';
import moment from 'moment';
import gaiaTheme from '../../../../root-components/theme';
import { IHeadCell } from '../../../../shared/interfaces/table-display.model';
import { IApiEndpoint, IApiKey, IApiKeyLog } from '../../../../shared/services/api-key/api-key.model';
import ApiKeyService from '../../../../shared/services/api-key/api-key.service';
import { TableDisplay } from '../../../presentation-components/miscellaneous-components/table-display';
import { LoadingPage } from '../../loading-page';
import { PageOuterContainer } from '../../../../root-components/layouts/page-outer-container';
import { PageHeading } from '../../../presentation-components/display-elements-components/page-heading';
import { CustomTabPanel } from '../../../presentation-components/display-elements-components/custom-tab-panel';
import { IconName } from '../../../../shared/interfaces/icon-picker.model';
import { UserNotificationsContext } from '../../../../context/user-notifications/user-notifications';
import { a11yProps } from '../../../../shared/utils/misc-shared-utils';

const BODY_PREVIEW_LENGTH = 64;

const methodColor = (method: string): 'success' | 'primary' | 'error' | 'warning' | 'default' => {
  switch (method.toUpperCase()) {
    case 'GET': return 'success';
    case 'POST': return 'primary';
    case 'DELETE': return 'error';
    case 'PATCH':
    case 'PUT': return 'warning';
    default: return 'default';
  }
};

export const ApiKeyDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const apiKeyService = new ApiKeyService();
  const userNotificationsContext = useContext(UserNotificationsContext);

  const [apiKey, setApiKey] = useState<IApiKey>();
  const [allEndpoints, setAllEndpoints] = useState<IApiEndpoint[]>([]);
  const [logs, setLogs] = useState<IApiKeyLog[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isLogsLoading, setIsLogsLoading] = useState<boolean>(true);
  const [selectedLog, setSelectedLog] = useState<IApiKeyLog | null>(null);

  const [selectedEndpointIds, setSelectedEndpointIds] = useState<string[]>([]);
  const [isAddingEndpoint, setIsAddingEndpoint] = useState<boolean>(false);

  const [endpointsInputPage, setEndpointsInputPage] = useState(0);
  const [endpointsPerPage, setEndpointsPerPage] = useState(10);

  const [logsSelected, setLogsSelected] = useState<string[]>([]);
  const [logsInputPage, setLogsInputPage] = useState(0);
  const [logsTotalRecords, setLogsTotalRecords] = useState(0);
  const [logsPerPage, setLogsPerPage] = useState(10);
  const [tabValue, setTabValue] = useState(0);

  const isMobileMode = useMediaQuery(gaiaTheme.breakpoints.down('md'));

  useEffect(() => {
    if (id) {
      getApiKey();
      getAllEndpoints();
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      getLogs();
    }
  }, [id, logsInputPage, logsPerPage]);

  const getApiKey = () => {
    if (!id) return;
    apiKeyService.getOne(id)
      .then(response => {
        setIsLoading(false);
        setApiKey(response);
      })
      .catch(err => {
        setIsLoading(false);
        console.log(err);
      });
  };

  const getLogs = () => {
    if (!id) return;
    setIsLogsLoading(true);
    apiKeyService.getLogs(id, logsInputPage, logsPerPage)
      .then(response => {
        setIsLogsLoading(false);
        setLogs(response.rows ?? []);
        setLogsTotalRecords(response.count ?? 0);
      })
      .catch(err => {
        setIsLogsLoading(false);
        console.log(err);
      });
  };

  const getAllEndpoints = () => {
    apiKeyService.getAllEndpoints()
      .then(response => {
        const data = Array.isArray(response) ? response : (response as any).rows ?? [];
        setAllEndpoints(data);
      })
      .catch(err => console.log(err));
  };

  const handleAddEndpoints = (idsToAdd: string[]) => {
    if (!id || idsToAdd.length === 0) return;
    setIsAddingEndpoint(true);
    apiKeyService.addEndpoints(id, idsToAdd)
      .then(() => {
        setIsAddingEndpoint(false);
        setSelectedEndpointIds([]);
        setEndpointsInputPage(0);
        getApiKey();
        userNotificationsContext.openSnackbar({ open: true, message: `${idsToAdd.length === 1 ? 'Endpoint' : `${idsToAdd.length} endpoints`} added successfully` });
      })
      .catch(err => {
        setIsAddingEndpoint(false);
        console.log(err);
        userNotificationsContext.openSnackbar({
          open: true,
          message: 'There was an error adding the endpoint(s), please try again',
          backgroundColor: 'text.primary',
        });
      });
  };

  const handleRemoveEndpoint = (endpoint: IApiEndpoint) => {
    if (!id) return;
    userNotificationsContext.openDialog({
      active: true,
      title: 'Remove Endpoint',
      message: `Are you sure you want to remove ${endpoint.method.toUpperCase()} ${endpoint.route} from this API key?`,
      buttonCopy: 'Remove',
      cancelButtonCopy: 'Cancel',
    }).then(confirmed => {
      if (confirmed) {
        apiKeyService.removeEndpoint(id, endpoint.id)
          .then(() => {
            getApiKey();
            userNotificationsContext.openSnackbar({ open: true, message: 'Endpoint removed successfully' });
          })
          .catch(err => {
            console.log(err);
            userNotificationsContext.openSnackbar({
              open: true,
              message: 'There was an error removing the endpoint, please try again',
              backgroundColor: 'text.primary',
            });
          });
      }
    });
  };

  const handleDelete = () => {
    if (!id) return;
    userNotificationsContext.openDialog({
      active: true,
      title: 'Delete API Key',
      message: 'Are you sure you want to delete this API key? This action cannot be undone and any integrations using this key will stop working.',
      buttonCopy: 'Delete',
      cancelButtonCopy: 'Cancel',
    }).then(confirmed => {
      if (confirmed) {
        apiKeyService.remove(id)
          .then(() => {
            userNotificationsContext.openSnackbar({ open: true, message: 'API key deleted successfully' });
            navigate('/settings/company-settings/api-keys');
          })
          .catch(err => {
            userNotificationsContext.openSnackbar({
              open: true,
              message: 'There was an error deleting the key, please try again',
              backgroundColor: 'text.primary',
            });
            console.log(err);
          });
      }
    });
  };

  const truncateBody = (body?: string): string => {
    if (!body) return '—';
    return body.length > BODY_PREVIEW_LENGTH ? body.slice(0, BODY_PREVIEW_LENGTH) + '…' : body;
  };

  // Endpoints already assigned to this key
  const assignedEndpoints: IApiEndpoint[] = apiKey?.endpoints ?? [];
  const assignedIds = new Set(assignedEndpoints.map(e => e.id));

  // Only show unassigned endpoints in the dropdown
  const availableEndpoints = allEndpoints.filter(e => !assignedIds.has(e.id));

  const groupDisplayNames: Record<string, string> = {
    'contact-supplier': 'Supplier',
    'finished-product': 'Product',
  };

  // Group endpoints by the first segment of their route (e.g. "/transactions/..." → "transactions")
  const groupedAvailableEndpoints = availableEndpoints.reduce<Record<string, IApiEndpoint[]>>((acc, ep) => {
    const firstSegment = ep.route.replace(/^\//, '').split('/')[0] || 'other';
    if (!acc[firstSegment]) acc[firstSegment] = [];
    acc[firstSegment].push(ep);
    return acc;
  }, {});
  const endpointGroups = Object.entries(groupedAvailableEndpoints).sort(([a], [b]) => a.localeCompare(b));

  // Endpoints table
  const endpointHeadCells: IHeadCell[] = [
    { id: 'method', numeric: false, label: 'Method', sortable: false },
    { id: 'route', numeric: false, label: 'Route', sortable: false, isHeading: true },
    { id: 'remove', numeric: false, label: '', sortable: false },
  ];

  const pagedAssignedEndpoints = assignedEndpoints.slice(
    endpointsInputPage * endpointsPerPage,
    (endpointsInputPage + 1) * endpointsPerPage,
  );

  const endpointRows = pagedAssignedEndpoints.map(ep => ({
    id: ep.id,
    method: (
      <Chip
        label={ep.method.toUpperCase()}
        size='small'
        variant='outlined'
        color={methodColor(ep.method)}
      />
    ),
    route: (
      <Typography variant='body2' sx={{ fontFamily: 'monospace' }}>
        {ep.route}
      </Typography>
    ),
    remove: (
      <Tooltip title='Remove endpoint'>
        <IconButton
          size='small'
          onClick={(e) => {
            e.stopPropagation();
            handleRemoveEndpoint(ep);
          }}
        >
          <DeleteOutline fontSize='small' />
        </IconButton>
      </Tooltip>
    ),
  }));

  // Logs table
  const logHeadCells: IHeadCell[] = [
    { id: 'createdAt', numeric: false, label: 'Date', sortable: false },
    { id: 'method', numeric: false, label: 'Method', sortable: false },
    { id: 'endpoint', numeric: false, label: 'Endpoint', sortable: false, isHeading: true },
    { id: 'body', numeric: false, label: 'Body', sortable: false, includeInMobile: false },
    { id: 'view', numeric: false, label: '', sortable: false },
  ];

  const logRows = logs.map(log => ({
    id: log.id,
    createdAt: moment(log.createdAt).format('DD/MM/YYYY HH:mm'),
    method: log.method ? (
      <Chip
        label={log.method.toUpperCase()}
        size='small'
        variant='outlined'
        color={methodColor(log.method)}
      />
    ) : '—',
    endpoint: log.endpoint || '—',
    body: (
      <Typography
        variant='body2'
        sx={{
          fontFamily: 'monospace',
          fontSize: '0.75rem',
          color: 'text.secondary',
          maxWidth: 200,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {truncateBody(log.body)}
      </Typography>
    ),
    view: (
      <Tooltip title='See details'>
        <IconButton
          size='small'
          onClick={(e) => {
            e.stopPropagation();
            setSelectedLog(log);
          }}
        >
          <Visibility fontSize='small' />
        </IconButton>
      </Tooltip>
    ),
  }));

  return (
    <PageOuterContainer>
      <LoadingPage pageReady={!isLoading}>
        {apiKey && (
          <>
            <PageHeading
              title={apiKey.details || 'API Key'}
              includeBackButton={true}
              backButtonLink='/settings/company-settings/api-keys'
              backButtonText='Back to API Keys'
              includeActionButton={true}
              actionButtonIcon={IconName.DeleteOutlineOutlined}
              actionButtonText='Delete Key'
              actionButtonHandler={handleDelete}
            />

            {/* Key details card */}
            <Box
              sx={{
                borderWidth: '1px',
                borderStyle: 'solid',
                borderColor: 'lightGrey.main',
                borderRadius: '10px',
                mb: 4,
                p: 3,
              }}
            >
              <Stack spacing={2}>
                {apiKey.details && (
                  <Stack direction='row' spacing={2} alignItems='center'>
                    <Typography variant='subtitle1' sx={{ color: 'text.secondary', minWidth: 120 }}>
                      Description
                    </Typography>
                    <Typography variant='body1'>{apiKey.details}</Typography>
                  </Stack>
                )}
                <Stack direction='row' spacing={2} alignItems='center'>
                  <Typography variant='subtitle1' sx={{ color: 'text.secondary', minWidth: 120 }}>
                    Created
                  </Typography>
                  <Typography variant='body1'>
                    {moment(apiKey.createdAt).format('DD/MM/YYYY HH:mm')}
                  </Typography>
                </Stack>
                <Stack direction='row' spacing={2} alignItems='center'>
                  <Typography variant='subtitle1' sx={{ color: 'text.secondary', minWidth: 120 }}>
                    Last Used
                  </Typography>
                  {apiKey.lastUsed ? (
                    <Typography variant='body1'>
                      {moment(apiKey.lastUsed).format('DD/MM/YYYY HH:mm')}
                    </Typography>
                  ) : (
                    <Chip label='Never used' size='small' variant='outlined' />
                  )}
                </Stack>
              </Stack>
            </Box>

            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)} aria-label='api key tabs'>
                <Tab label='Allowed Endpoints' {...a11yProps(0)} />
                <Tab label='Request Logs' {...a11yProps(1)} />
              </Tabs>
            </Box>

            <CustomTabPanel value={tabValue} index={0}>
              {availableEndpoints.length > 0 && (
                <Stack direction={isMobileMode ? 'column' : 'row'} spacing={2} alignItems='flex-start' sx={{ mb: 3 }}>
                  <FormControl sx={{ minWidth: 340 }} size='small'>
                    <InputLabel id='add-endpoint-label'>Add endpoints</InputLabel>
                    <Select
                      labelId='add-endpoint-label'
                      multiple
                      value={selectedEndpointIds}
                      input={<OutlinedInput label='Add endpoints' />}
                      onChange={(e) => {
                        const val = e.target.value;
                        setSelectedEndpointIds(typeof val === 'string' ? val.split(',') : val);
                      }}
                      disabled={isAddingEndpoint}
                      renderValue={(selected) => {
                        const count = (selected as string[]).length;
                        return count === 0 ? '' : `${count} endpoint${count > 1 ? 's' : ''} selected`;
                      }}
                    >
                      {endpointGroups.flatMap(([group, endpoints]) => [
                        <ListSubheader key={`group-${group}`} sx={{ fontWeight: 600, textTransform: 'capitalize', lineHeight: '32px' }}>
                          {groupDisplayNames[group] ?? group}
                        </ListSubheader>,
                        ...endpoints.map(ep => (
                          <MenuItem key={ep.id} value={ep.id}>
                            <Checkbox checked={selectedEndpointIds.includes(ep.id)} size='small' />
                            <Stack direction='row' spacing={1} alignItems='center'>
                              <Chip
                                label={ep.method.toUpperCase()}
                                size='small'
                                variant='outlined'
                                color={methodColor(ep.method)}
                                sx={{ minWidth: 60 }}
                              />
                              <Typography variant='body2' sx={{ fontFamily: 'monospace' }}>
                                {ep.route}
                              </Typography>
                            </Stack>
                          </MenuItem>
                        )),
                      ])}
                    </Select>
                  </FormControl>
                  <Button
                    variant='contained'
                    disabled={selectedEndpointIds.length === 0 || isAddingEndpoint}
                    onClick={() => handleAddEndpoints(selectedEndpointIds)}
                  >
                    Add
                  </Button>
                  <Button
                    variant='outlined'
                    disabled={isAddingEndpoint}
                    onClick={() => handleAddEndpoints(availableEndpoints.map(ep => ep.id))}
                  >
                    Add all endpoints
                  </Button>
                </Stack>
              )}

              <TableDisplay
                isMobileMode={isMobileMode}
                headCells={endpointHeadCells}
                rows={endpointRows}
                haveCheckboxSelect={false}
                selected={[]}
                setSelected={() => {}}
                inputPage={endpointsInputPage}
                setInputPage={setEndpointsInputPage}
                totalRecords={assignedEndpoints.length}
                perPage={endpointsPerPage}
                setPerPage={setEndpointsPerPage}
              />
            </CustomTabPanel>

            <CustomTabPanel value={tabValue} index={1}>
              <LoadingPage pageReady={!isLogsLoading}>
                <TableDisplay
                  isMobileMode={isMobileMode}
                  headCells={logHeadCells}
                  rows={logRows}
                  haveCheckboxSelect={false}
                  selected={logsSelected}
                  setSelected={setLogsSelected}
                  inputPage={logsInputPage}
                  setInputPage={setLogsInputPage}
                  totalRecords={logsTotalRecords}
                  perPage={logsPerPage}
                  setPerPage={setLogsPerPage}
                />
              </LoadingPage>
            </CustomTabPanel>
          </>
        )}
      </LoadingPage>

      {/* Log detail dialog */}
      {selectedLog && (
        <Dialog open={true} onClose={() => setSelectedLog(null)} maxWidth='sm' fullWidth>
          <Box sx={{ p: 1 }}>
            <DialogTitle>Log Details</DialogTitle>
            <DialogContent>
              <Stack spacing={2}>
                <Stack direction='row' spacing={2}>
                  <Typography variant='subtitle2' sx={{ color: 'text.secondary', minWidth: 100 }}>
                    Date
                  </Typography>
                  <Typography variant='body2'>
                    {moment(selectedLog.createdAt).format('DD/MM/YYYY HH:mm:ss')}
                  </Typography>
                </Stack>
                {selectedLog.method && (
                  <Stack direction='row' spacing={2} alignItems='center'>
                    <Typography variant='subtitle2' sx={{ color: 'text.secondary', minWidth: 100 }}>
                      Method
                    </Typography>
                    <Chip
                      label={selectedLog.method.toUpperCase()}
                      size='small'
                      variant='outlined'
                      color={methodColor(selectedLog.method)}
                    />
                  </Stack>
                )}
                <Stack direction='row' spacing={2}>
                  <Typography variant='subtitle2' sx={{ color: 'text.secondary', minWidth: 100 }}>
                    Endpoint
                  </Typography>
                  <Typography variant='body2' sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                    {selectedLog.endpoint || '—'}
                  </Typography>
                </Stack>
                <Stack direction='column' spacing={1}>
                  <Typography variant='subtitle2' sx={{ color: 'text.secondary' }}>
                    Body
                  </Typography>
                  <Box
                    sx={{
                      bgcolor: 'grey.100',
                      borderRadius: 1,
                      p: 1.5,
                      maxHeight: 300,
                      overflowY: 'auto',
                    }}
                  >
                    <Typography
                      variant='body2'
                      sx={{ fontFamily: 'monospace', fontSize: '0.75rem', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}
                    >
                      {selectedLog.body || '—'}
                    </Typography>
                  </Box>
                </Stack>
              </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button variant='contained' onClick={() => setSelectedLog(null)}>
                Close
              </Button>
            </DialogActions>
          </Box>
        </Dialog>
      )}
    </PageOuterContainer>
  );
};
